// Top level libraries and 3rd party dependencies
import React, { Component } from 'react';
import { func, object, string } from 'prop-types';
import moment from 'moment';
import { contains, isEmpty } from 'ramda';

import { DATE_FORMAT, MAX_RETRY_COUNT, DEFAULT_HIPAA_SAFE_LABEL } from '../../constants/AppConstants';
import Layout from '../Layout';
import SavePIN from '../SavePIN';
import LoadingState from '../LoadingState';
import ErrorMessage from '../ErrorMessage';
import * as SessionTypes from '../../constants/ActionTypes';
import Footer from '../Footer';

// Page styles
import cx from 'classnames';
import s from './Verify.css';


// Updox Patients require validating their DOB and Last name to proceed with creating a PIN for the first time
export default class VerifyPatient extends Component {
    static propTypes = {
        conversationHash: string.isRequired,
        loadingState: object,
        conversationDetails: object.isRequired,
        savePIN: func.isRequired,
        session: object,
        verifyDOB: func.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            month: '',
            day: '',
            year: '',
            lastName: '',
        };

        // Refs are declared in the render, so inputs is not a state variable to avoid a loop
        this.inputs = {};
    }

    componentDidMount() {
        document.title = 'Verify Identity - Updox Secure Text';
    }

    /**
     * If you only want to know whether a date string was successfully converted into a proper date object,
     * you can invoke the isValid() method on the moment instance variable:
     * var aDate = moment(dateElt.value, dateFormat, true);
     * var isValid = aDate.isValid();
     */
    buildDate = (year, month, day) => moment(`${year}-${month}-${day}`, DATE_FORMAT, true);

    // moment expects 'MM/DD/YYYY' so we force double digit
    handleSingleDigitMonth = event => {
        let value = event.target.value;
        if (value.length === 1) {
            value = `0${value}`;
            this.setState({ month: value});
        }
    };

    // moment expects 'MM/DD/YYYY' so we force double digit
    handleSingleDigitDay = event => {
        let value = event.target.value;
        if (value.length === 1) {
            value = `0${value}`;
            this.setState({ day: value});
        }
    };

    handleChange = event => this.setState({ [event.target.name]: event.target.value });

    // Auto-tab inputs at maxlength, unless it's backspace, delete, shift, tab, or an arrow key
    focusInput = (event, id) => {
        const allowedMaxInputKeys = [8, 9, 16, 37, 38, 39, 40, 46];

        if (event.target.value.length >= event.target.maxLength && allowedMaxInputKeys.indexOf(event.keyCode) === -1) {
            this.inputs[id].focus();
        }
    };

    // Tests will fail if we do not give this Ramda method what it expects, an array. this.props.loadingState.requests returns an array.
    isLoadingVerifyDOB = loadingState => contains(SessionTypes.VERIFY_DOB_REQUEST, loadingState.requests || []);

    handleSubmit = e => {
        e.preventDefault();

        const { conversationHash, verifyDOB } = this.props;
        const { month, day, year, lastName } = this.state;
        const date = this.buildDate(year, month, day);

        if (this.canBeSubmitted()) {
            const request = {
                conversationHash,
                dateOfBirth: date._i,
                lastName,
            };

            verifyDOB(request);
            e.target.blur();
        }
    };

    /**
     *  Ensure we have a valid moment date object as well as actual values in state.
     *  moment.js validation with isValid().
     **/
    canBeSubmitted = () => {
        const { conversationHash } = this.props;
        const { month, day, year, lastName } = this.state;
        const dateIsValid = this.buildDate(year, month, day).isValid();

        return (
            month.length > 0 &&
            day.length > 0 &&
            year.length > 0 &&
            lastName.length > 0 &&
            !isEmpty(conversationHash) &&
            dateIsValid
        );
    };

    render() {
        const {
            conversationHash,
            loadingState,
            conversationDetails,
            savePIN,
            session,
        } = this.props;

        const isLoadingVerifyDOB = this.isLoadingVerifyDOB(loadingState);
        const isEnabled = this.canBeSubmitted();

        const hipaaSafePracticeName = `${conversationDetails.practiceName}` === DEFAULT_HIPAA_SAFE_LABEL
            ? `your ` + DEFAULT_HIPAA_SAFE_LABEL : `${conversationDetails.practiceName}`;
        const hasConversationDetails = !isEmpty(conversationDetails);
        const lockedOut = session.error ? session.error.retryCount >= MAX_RETRY_COUNT : false;
        const additionalLockedOutCopy = `${conversationDetails.practiceName}` === DEFAULT_HIPAA_SAFE_LABEL
            ? `Your ${conversationDetails.practiceName} has been notified of the failure to authenticate.`
            : `${conversationDetails.practiceName} has been notified of the failure to authenticate.`;
        const additionalPracticePhoneCopy = conversationDetails.practicePhone ? ` at ${conversationDetails.practicePhone}.` : '.';

        return (
            <Layout centered className={s.layout}>
                {!session.verifiedDOB &&
                    <form
                        className={cx(s.component, session.error ? 'hasErrors' : '')}
                        id="verifyForm"
                        onSubmit={this.handleSubmit}
                     >
                        <input type="hidden" value="trickChromesAutoCompleteBullshit" />

                        {hasConversationDetails && !lockedOut &&
                            <div id="conversationDetails" className={cx(s.alignItems, s.componentBlock, s.verticalSpacing, s.heading)}>
                                <h3>{conversationDetails.practiceName}</h3>
                                <label>{conversationDetails.practicePhone}</label>
                            </div>
                        }

                        {!lockedOut && !conversationDetails.error && hasConversationDetails &&
                            <React.Fragment>
                                <hr className={s.divider}/>

                                <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing, s.heading)}>
                                    <p>Please confirm your date of birth and last name to view your secure medical information.</p>
                                </div>

                                <div className={cx('formGroup', s.verticalSpacing)}>
                                    <div className={s.componentBlock}>
                                        <input
                                            id="month"
                                            type="number"
                                            pattern="[0-9]*"
                                            name="month"
                                            ref={input => {
                                                this.inputs['month'] = input;
                                            }}
                                            value={this.state.month}
                                            onBlur={this.handleSingleDigitMonth}
                                            onChange={this.handleChange}
                                            onKeyUp={(e) => this.focusInput(e, 'day')}
                                            className="inputControl"
                                            min="0"
                                            maxLength={2}
                                            required
                                        />
                                        <label
                                            htmlFor="month"
                                            className={s.heading}
                                        >
                                            <span>Month</span>
                                        </label>
                                    </div>
                                    <div className={s.componentBlock}>
                                        <input
                                            id="day"
                                            type="number"
                                            pattern="[0-9]*"
                                            name="day"
                                            ref={input => {
                                                this.inputs['day'] = input;
                                            }}
                                            value={this.state.day}
                                            onBlur={this.handleSingleDigitDay}
                                            onChange={this.handleChange}
                                            onKeyUp={(e) => this.focusInput(e, 'year')}
                                            className="inputControl"
                                            min="0"
                                            maxLength={2}
                                            required
                                        />
                                        <label
                                            htmlFor="day"
                                            className={s.heading}
                                        >
                                            <span>Day</span>
                                        </label>
                                    </div>
                                    <div className={s.componentBlock}>
                                        <input
                                            id="year"
                                            type="number"
                                            pattern="[0-9]*"
                                            name="year"
                                            ref={input => {
                                                this.inputs['year'] = input;
                                            }}
                                            value={this.state.year}
                                            onChange={this.handleChange}
                                            onKeyUp={(e) => this.focusInput(e, 'lastName')}
                                            className="inputControl"
                                            min="0"
                                            maxLength={4}
                                            required
                                        />
                                        <label
                                            htmlFor="year"
                                            className={s.heading}
                                        >
                                            <span>Year</span>
                                        </label>
                                    </div>
                                </div>

                                <div className={cx('formGroup', s.verticalSpacing)}>
                                    <div className={cx(s.componentBlock, 'fullWidth')}>
                                        <input
                                            id="lastName"
                                            type="text"
                                            name="lastName"
                                            ref={input => {
                                                this.inputs['lastName'] = input;
                                            }}
                                            value={this.state.lastName}
                                            onChange={this.handleChange}
                                            className="inputControl"
                                            autoComplete="family-name"
                                            required
                                        />
                                        <label
                                            htmlFor="lastName"
                                            className={s.heading}
                                        >
                                            <span>Last Name</span>
                                        </label>
                                    </div>
                                </div>
                            </React.Fragment>
                        }

                        {/* Handle situation where the conversation does not exist, i.e. bad/fake url */}
                        {session.error && session.error.message && isEmpty(conversationDetails) &&
                            <ErrorMessage
                                title={`${session.error.message}`}
                            />
                        }

                        {/* Generic message if Catalyst is down */}
                        {session.error && session.error.message === null &&
                            <ErrorMessage
                                title="There is a problem"
                                message="If you have any questions or concerns, contact Updox Support at updox.com/support."
                            />
                        }

                        {/* Handle authentication error */}
                        {session.error && hasConversationDetails &&
                            <ErrorMessage
                                title="Failure to authenticate"
                                message={`${session.error.message} ${lockedOut ? additionalLockedOutCopy : ''} If you have any questions or concerns, please contact ${hipaaSafePracticeName}${additionalPracticePhoneCopy}`}
                            />
                        }

                        {!lockedOut && hasConversationDetails &&
                            <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing)}>
                                <button
                                    className={cx(s.btn, s.primary)}
                                    id="submitVerify"
                                    type="submit"
                                    disabled={!isEnabled || isLoadingVerifyDOB}
                                >
                                    {isLoadingVerifyDOB ?
                                        <LoadingState context="verifyForm"/> :
                                        <span className={s.btnText}>Verify Identity</span>
                                    }
                                </button>
                            </div>
                        }
                        <Footer/>
                    </form>
                }

                {/* First-time PIN Flow */}
                {hasConversationDetails && !isEmpty(conversationHash) && session.verifiedDOB &&
                    <SavePIN
                        conversationHash={conversationHash}
                        error={session.error}
                        loadingState={loadingState}
                        conversationDetails={conversationDetails}
                        savePIN={savePIN}
                    />
                }
            </Layout>
        );
    }
}
